import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { Icons } from '../components/Icons';

function FilterManagement() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('category');
  const [_loading, setLoading] = useState(false); // eslint-disable-line no-unused-vars

  // 필터 데이터
  const [categoriesLarge, setCategoriesLarge] = useState([]);
  const [categoriesMedium, setCategoriesMedium] = useState([]);
  const [statusesLarge, setStatusesLarge] = useState([]);
  const [statusesMedium, setStatusesMedium] = useState([]);
  const [conditions, setConditions] = useState([]);

  // 폼 상태
  const [newCategoryLarge, setNewCategoryLarge] = useState('');
  const [newCategoryMedium, setNewCategoryMedium] = useState('');
  const [selectedParentCategory, setSelectedParentCategory] = useState('');
  const [newStatusLarge, setNewStatusLarge] = useState('');
  const [newStatusMedium, setNewStatusMedium] = useState('');
  const [selectedParentStatus, setSelectedParentStatus] = useState('');
  const [newCondition, setNewCondition] = useState('');

  // 검색
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    setLoading(true);
    try {
      // 카테고리(대) 불러오기
      const categoriesLargeRef = collection(db, 'filter_categories_large');
      const categoriesLargeSnapshot = await getDocs(categoriesLargeRef);
      const categoriesLargeList = categoriesLargeSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategoriesLarge(categoriesLargeList);

      // 카테고리(중) 불러오기
      const categoriesMediumRef = collection(db, 'filter_categories_medium');
      const categoriesMediumSnapshot = await getDocs(categoriesMediumRef);
      const categoriesMediumList = categoriesMediumSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategoriesMedium(categoriesMediumList);

      // 상태(대) 불러오기
      const statusesLargeRef = collection(db, 'filter_statuses_large');
      const statusesLargeSnapshot = await getDocs(statusesLargeRef);
      const statusesLargeList = statusesLargeSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStatusesLarge(statusesLargeList);

      // 상태(중) 불러오기
      const statusesMediumRef = collection(db, 'filter_statuses_medium');
      const statusesMediumSnapshot = await getDocs(statusesMediumRef);
      const statusesMediumList = statusesMediumSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStatusesMedium(statusesMediumList);

      // 컨디션 불러오기
      const conditionsRef = collection(db, 'filter_conditions');
      const conditionsSnapshot = await getDocs(conditionsRef);
      const conditionsList = conditionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setConditions(conditionsList);
    } catch (error) {
      console.error('필터 옵션 불러오기 오류:', error);
      alert('필터 옵션을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategoryLarge = async (e) => {
    e.preventDefault();
    if (!newCategoryLarge.trim()) {
      alert('카테고리(대)를 입력하세요.');
      return;
    }

    try {
      await addDoc(collection(db, 'filter_categories_large'), {
        name: newCategoryLarge.trim(),
        createdAt: serverTimestamp(),
        createdBy: user.email
      });
      setNewCategoryLarge('');
      fetchFilterOptions();
      alert('✅ 카테고리(대)가 추가되었습니다.');
    } catch (error) {
      console.error('카테고리 추가 오류:', error);
      alert('❌ 카테고리 추가 중 오류가 발생했습니다.');
    }
  };

  const handleAddCategoryMedium = async (e) => {
    e.preventDefault();
    if (!newCategoryMedium.trim()) {
      alert('카테고리(중)을 입력하세요.');
      return;
    }
    if (!selectedParentCategory) {
      alert('상위 카테고리(대)를 선택하세요.');
      return;
    }

    try {
      await addDoc(collection(db, 'filter_categories_medium'), {
        name: newCategoryMedium.trim(),
        parentCategory: selectedParentCategory,
        createdAt: serverTimestamp(),
        createdBy: user.email
      });
      setNewCategoryMedium('');
      setSelectedParentCategory('');
      fetchFilterOptions();
      alert('✅ 카테고리(중)이 추가되었습니다.');
    } catch (error) {
      console.error('카테고리 추가 오류:', error);
      alert('❌ 카테고리 추가 중 오류가 발생했습니다.');
    }
  };

  const handleAddStatusLarge = async (e) => {
    e.preventDefault();
    if (!newStatusLarge.trim()) {
      alert('상태(대)를 입력하세요.');
      return;
    }

    try {
      await addDoc(collection(db, 'filter_statuses_large'), {
        name: newStatusLarge.trim(),
        createdAt: serverTimestamp(),
        createdBy: user.email
      });
      setNewStatusLarge('');
      fetchFilterOptions();
      alert('✅ 상태(대)가 추가되었습니다.');
    } catch (error) {
      console.error('상태 추가 오류:', error);
      alert('❌ 상태 추가 중 오류가 발생했습니다.');
    }
  };

  const handleAddStatusMedium = async (e) => {
    e.preventDefault();
    if (!newStatusMedium.trim()) {
      alert('상태(중)을 입력하세요.');
      return;
    }
    if (!selectedParentStatus) {
      alert('상위 상태(대)를 선택하세요.');
      return;
    }

    try {
      await addDoc(collection(db, 'filter_statuses_medium'), {
        name: newStatusMedium.trim(),
        parentStatus: selectedParentStatus,
        createdAt: serverTimestamp(),
        createdBy: user.email
      });
      setNewStatusMedium('');
      setSelectedParentStatus('');
      fetchFilterOptions();
      alert('✅ 상태(중)이 추가되었습니다.');
    } catch (error) {
      console.error('상태 추가 오류:', error);
      alert('❌ 상태 추가 중 오류가 발생했습니다.');
    }
  };

  const handleAddCondition = async (e) => {
    e.preventDefault();
    if (!newCondition.trim()) {
      alert('컨디션명을 입력하세요.');
      return;
    }

    try {
      await addDoc(collection(db, 'filter_conditions'), {
        name: newCondition.trim(),
        createdAt: serverTimestamp(),
        createdBy: user.email
      });
      setNewCondition('');
      fetchFilterOptions();
      alert('✅ 컨디션이 추가되었습니다.');
    } catch (error) {
      console.error('컨디션 추가 오류:', error);
      alert('❌ 컨디션 추가 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = async (collectionName, id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      await deleteDoc(doc(db, collectionName, id));
      fetchFilterOptions();
      alert('✅ 삭제되었습니다.');
    } catch (error) {
      console.error('삭제 오류:', error);
      alert('❌ 삭제 중 오류가 발생했습니다.');
    }
  };

  const filteredCategoriesLarge = categoriesLarge.filter(cat =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredCategoriesMedium = categoriesMedium.filter(cat =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredStatusesLarge = statusesLarge.filter(status =>
    status.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredStatusesMedium = statusesMedium.filter(status =>
    status.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredConditions = conditions.filter(cond =>
    cond.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { id: 'category', label: '카테고리', icon: Icons.Tag },
    { id: 'status', label: '상태', icon: Icons.Check },
    { id: 'condition', label: '컨디션', icon: Icons.Star }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold text-brand-900">필터 항목 관리</h2>
        <p className="text-sm text-gray-500">재고 목록에서 사용할 필터 옵션을 관리합니다.</p>
      </div>

      {/* 탭 메뉴 */}
      <div className="bg-white rounded-xl shadow-sm border border-brand-100 mb-6">
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                  activeTab === tab.id
                    ? 'text-brand-700 border-b-2 border-brand-600 bg-brand-50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <TabIcon className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 카테고리 관리 */}
      {activeTab === 'category' && (
        <div className="space-y-6">
          {/* 카테고리(대) */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <h3 className="text-xl font-bold text-brand-900 mb-6 flex items-center">
              <Icons.Tag className="w-6 h-6 mr-2 text-brand-600" />
              카테고리(대) 관리
            </h3>

            <form onSubmit={handleAddCategoryLarge} className="mb-6">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newCategoryLarge}
                  onChange={(e) => setNewCategoryLarge(e.target.value)}
                  placeholder="예: SEATING, TABLE, LIGHTING"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition font-medium flex items-center"
                >
                  <Icons.Add className="w-5 h-5 mr-2" />
                  추가
                </button>
              </div>
            </form>

            <div className="space-y-2">
              {filteredCategoriesLarge.length === 0 ? (
                <p className="text-center text-gray-500 py-8">등록된 카테고리(대)가 없습니다.</p>
              ) : (
                filteredCategoriesLarge.map((category) => (
                  <div
                    key={category.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <span className="font-medium text-gray-900">{category.name}</span>
                    <button
                      onClick={() => handleDelete('filter_categories_large', category.id)}
                      className="text-red-600 hover:text-red-700 p-2"
                    >
                      <Icons.Delete className="w-5 h-5" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* 카테고리(중) */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <h3 className="text-xl font-bold text-brand-900 mb-6 flex items-center">
              <Icons.Tag className="w-6 h-6 mr-2 text-brand-600" />
              카테고리(중) 관리
            </h3>

            <form onSubmit={handleAddCategoryMedium} className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <select
                  value={selectedParentCategory}
                  onChange={(e) => setSelectedParentCategory(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
                  required
                >
                  <option value="">상위 카테고리(대) 선택</option>
                  {categoriesLarge.map((cat) => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={newCategoryMedium}
                  onChange={(e) => setNewCategoryMedium(e.target.value)}
                  placeholder="예: CHAIR, SOFA, WALL LAMP"
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <button
                type="submit"
                className="w-full px-6 py-3 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition font-medium flex items-center justify-center"
              >
                <Icons.Add className="w-5 h-5 mr-2" />
                추가
              </button>
            </form>

            <div className="space-y-6">
              {filteredCategoriesMedium.length === 0 ? (
                <p className="text-center text-gray-500 py-8">등록된 카테고리(중)이 없습니다.</p>
              ) : (
                categoriesLarge.map((largeCat) => {
                  const mediumCatsInGroup = filteredCategoriesMedium.filter(
                    cat => cat.parentCategory === largeCat.name
                  );

                  if (mediumCatsInGroup.length === 0) return null;

                  return (
                    <div key={largeCat.id} className="border-l-4 border-brand-400 pl-4">
                      <h4 className="font-bold text-brand-700 mb-3 flex items-center">
                        <Icons.Tag className="w-5 h-5 mr-2" />
                        {largeCat.name}
                      </h4>
                      <div className="space-y-2">
                        {mediumCatsInGroup.map((category) => (
                          <div
                            key={category.id}
                            className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition"
                          >
                            <span className="font-medium text-gray-900">{category.name}</span>
                            <button
                              onClick={() => handleDelete('filter_categories_medium', category.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            >
                              <Icons.Delete className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* 상태 관리 */}
      {activeTab === 'status' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 상태(대) */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <h3 className="text-xl font-bold text-brand-900 mb-6 flex items-center">
              <Icons.Check className="w-6 h-6 mr-2 text-brand-600" />
              상태(대) 관리
            </h3>

            <form onSubmit={handleAddStatusLarge} className="mb-6">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newStatusLarge}
                  onChange={(e) => setNewStatusLarge(e.target.value)}
                  placeholder="예: 판매가능, 판매완료, 수리중"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <button type="submit" className="px-6 py-3 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition font-medium flex items-center">
                  <Icons.Add className="w-5 h-5 mr-2" />
                  추가
                </button>
              </div>
            </form>

            <div className="space-y-2">
              {filteredStatusesLarge.length === 0 ? (
                <p className="text-center text-gray-500 py-8">등록된 상태(대)가 없습니다.</p>
              ) : (
                filteredStatusesLarge.map((status) => (
                  <div key={status.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="font-medium text-gray-900">{status.name}</span>
                    <button
                      onClick={() => handleDelete('filter_statuses_large', status.id)}
                      className="text-red-600 hover:text-red-700 p-2"
                    >
                      <Icons.Delete className="w-5 h-5" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* 상태(중) */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
            <h3 className="text-xl font-bold text-brand-900 mb-6 flex items-center">
              <Icons.Check className="w-6 h-6 mr-2 text-brand-600" />
              상태(중) 관리
            </h3>

            <form onSubmit={handleAddStatusMedium} className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <select
                  value={selectedParentStatus}
                  onChange={(e) => setSelectedParentStatus(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
                  required
                >
                  <option value="">상위 상태(대) 선택</option>
                  {statusesLarge.map((cat) => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={newStatusMedium}
                  onChange={(e) => setNewStatusMedium(e.target.value)}
                  placeholder="예: 오일링 후 판매, 수리 대기"
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <button
                type="submit"
                className="w-full px-6 py-3 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition font-medium flex items-center justify-center"
              >
                <Icons.Add className="w-5 h-5 mr-2" />
                추가
              </button>
            </form>

            <div className="space-y-6">
              {filteredStatusesMedium.length === 0 ? (
                <p className="text-center text-gray-500 py-8">등록된 상태(중)이 없습니다.</p>
              ) : (
                statusesLarge.map((largeStat) => {
                  const mediumStatsInGroup = filteredStatusesMedium.filter(
                    stat => stat.parentStatus === largeStat.name
                  );

                  if (mediumStatsInGroup.length === 0) return null;

                  return (
                    <div key={largeStat.id} className="border-l-4 border-brand-400 pl-4">
                      <h4 className="font-bold text-brand-700 mb-3 flex items-center">
                        <Icons.Check className="w-5 h-5 mr-2" />
                        {largeStat.name}
                      </h4>
                      <div className="space-y-2">
                        {mediumStatsInGroup.map((status) => (
                          <div
                            key={status.id}
                            className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition"
                          >
                            <span className="font-medium text-gray-900">{status.name}</span>
                            <button
                              onClick={() => handleDelete('filter_statuses_medium', status.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            >
                              <Icons.Delete className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* 컨디션 관리 */}
      {activeTab === 'condition' && (
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <h3 className="text-xl font-bold text-brand-900 mb-6 flex items-center">
            <Icons.Star className="w-6 h-6 mr-2 text-brand-600" />
            컨디션 관리
          </h3>

          <form onSubmit={handleAddCondition} className="mb-6">
            <div className="flex gap-3">
              <input
                type="text"
                value={newCondition}
                onChange={(e) => setNewCondition(e.target.value)}
                placeholder="새 컨디션 입력 (예: 최상, 상, 중, 하)"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition font-medium flex items-center"
              >
                <Icons.Add className="w-5 h-5 mr-2" />
                추가
              </button>
            </div>
          </form>

          <div className="mb-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="검색..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="space-y-2">
            {filteredConditions.length === 0 ? (
              <p className="text-center text-gray-500 py-8">등록된 컨디션이 없습니다.</p>
            ) : (
              filteredConditions.map((condition) => (
                <div
                  key={condition.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                >
                  <div className="flex items-center space-x-3">
                    <Icons.Star className="w-5 h-5 text-yellow-600" />
                    <span className="font-medium text-gray-900">{condition.name}</span>
                  </div>
                  <button
                    onClick={() => handleDelete('filter_conditions', condition.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                  >
                    <Icons.Delete className="w-5 h-5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default FilterManagement;
